import { attach, createEvent, createStore, sample } from 'effector'
import {
  NotificationPayload,
  showNotification,
} from 'src/features/notifications'
import { fx } from 'src/libs'
import { gatewayBlockChanged } from '../../blockGateway'
import {
  DETAILS_PAGE_SIZE,
  gatewayHistoryDetailsUrl,
  HISTORY_DEFAULT_ORDER,
} from '../../const'
import { GatewayHistoryRecord } from '../../types'
import {
  DetailsLoaded,
  DetailsLoadPayload,
  GatewayDetailsResponse,
} from './types'

// Internal only: carries a per-gateway sequence number alongside the request
// so a `.done` response can be checked against the most recently issued
// request for that gateway before it is allowed to reach the stores.
type DetailsRequest = DetailsLoadPayload & { seq: number }

// The only effect that talks to the network. It knows nothing but url and
// query, which is why it stays private.
const getGatewayDetailsFx = fx<void, GatewayDetailsResponse>({ method: 'GET' })

// Wraps the request so that `gatewayId` (and `seq`, see below) survive into
// `.done`. Several rows can be expanded at once and their responses arrive in
// any order — without this the id would have to be parsed back out of the
// url. `mapParams` still returns exactly `{ url, query }`; `seq` is only
// carried on `.done.params`, it never reaches the network.
export const loadGatewayDetailsFx = attach({
  effect: getGatewayDetailsFx,
  mapParams: ({ clientId, gatewayId, page }: DetailsRequest) => ({
    url: gatewayHistoryDetailsUrl(clientId, gatewayId),
    query: {
      page: String(page),
      limit: String(DETAILS_PAGE_SIZE),
      // The detail list always reads newest first, independently of how the
      // summary table above is sorted.
      order: HISTORY_DEFAULT_ORDER,
    },
  }),
})

export const gatewayRowExpanded = createEvent<{
  clientId: string
  gatewayId: number
}>()
export const gatewayRowCollapsed = createEvent<number>()
export const detailsPageChanged = createEvent<DetailsLoadPayload>()

const detailsLoaded = createEvent<DetailsLoaded>()

// Internal relay between the public "something needs loading" events and the
// effect: `requestIssued` carries the request as callers know it, then gets
// stamped with the next per-gateway sequence number before it reaches the
// effect as `requestWithSeq`.
const requestIssued = createEvent<DetailsLoadPayload>()
const requestWithSeq = createEvent<DetailsRequest>()

export const $expandedGatewayIds = createStore<number[]>([])
  .on(gatewayRowExpanded, (ids, { gatewayId }) =>
    ids.includes(gatewayId) ? ids : [...ids, gatewayId],
  )
  .on(gatewayRowCollapsed, (ids, gatewayId) =>
    ids.filter((id) => id !== gatewayId),
  )
  .reset(gatewayBlockChanged)

export const $detailsByGateway = createStore<
  Record<number, { items: GatewayHistoryRecord[]; total: number }>
>({})
  .on(detailsLoaded, (state, { gatewayId, items, total }) => ({
    ...state,
    [gatewayId]: { items, total },
  }))
  .reset(gatewayBlockChanged)

export const $pageByGateway = createStore<Record<number, number>>({})
  .on(detailsLoaded, (state, { gatewayId, page }) => ({
    ...state,
    [gatewayId]: page,
  }))
  .reset(gatewayBlockChanged)

// The last sequence number *issued* per gateway. Not reset on
// `gatewayBlockChanged`: it must keep climbing so a response from before the
// reset can never coincidentally match the sequence of a request issued
// after it.
const $requestSeqByGateway = createStore<Record<number, number>>({}).on(
  requestWithSeq,
  (seqByGateway, { gatewayId, seq }) => ({ ...seqByGateway, [gatewayId]: seq }),
)

// Counts in-flight requests per gateway rather than a plain id list, so that
// when two requests for the same gateway overlap (e.g. two page clicks), the
// row keeps showing as loading until the *last* one settles, not the first.
const $inFlightCountByGateway = createStore<Record<number, number>>({})
  .on(loadGatewayDetailsFx, (counts, { gatewayId }) => ({
    ...counts,
    [gatewayId]: (counts[gatewayId] ?? 0) + 1,
  }))
  .on(loadGatewayDetailsFx.finally, (counts, { params }) => ({
    ...counts,
    [params.gatewayId]: Math.max((counts[params.gatewayId] ?? 0) - 1, 0),
  }))

export const $loadingGatewayIds = $inFlightCountByGateway.map((counts) =>
  Object.entries(counts)
    .filter(([, count]) => count > 0)
    .map(([gatewayId]) => Number(gatewayId)),
)

// The first expand loads, a repeated one shows what is already cached.
sample({
  clock: gatewayRowExpanded,
  source: $detailsByGateway,
  filter: (cache, { gatewayId }) => !cache[gatewayId],
  fn: (_, { clientId, gatewayId }) => ({ clientId, gatewayId, page: 1 }),
  target: requestIssued,
})

sample({
  clock: detailsPageChanged,
  target: requestIssued,
})

// Stamp every request with the next sequence number for its gateway before
// it reaches the effect.
sample({
  clock: requestIssued,
  source: $requestSeqByGateway,
  fn: (seqByGateway, payload): DetailsRequest => ({
    ...payload,
    seq: (seqByGateway[payload.gatewayId] ?? 0) + 1,
  }),
  target: requestWithSeq,
})

sample({
  clock: requestWithSeq,
  target: loadGatewayDetailsFx,
})

// A response is only stored if its row is still expanded AND it belongs to
// the most recently issued request for that gateway. The expanded check
// drops responses for rows collapsed mid-flight; the sequence check drops
// stale responses left over from superseded pagination clicks, from a
// collapse immediately followed by re-expand, or from before a
// `gatewayBlockChanged` reset that a slower in-flight request outlived.
sample({
  clock: loadGatewayDetailsFx.done,
  source: { expandedIds: $expandedGatewayIds, seqByGateway: $requestSeqByGateway },
  filter: ({ expandedIds, seqByGateway }, { params }) =>
    expandedIds.includes(params.gatewayId) &&
    seqByGateway[params.gatewayId] === params.seq,
  fn: (_, { params, result }): DetailsLoaded => ({
    gatewayId: params.gatewayId,
    page: params.page,
    items: result.body.items,
    total: result.body.total,
  }),
  target: detailsLoaded,
})

sample({
  clock: loadGatewayDetailsFx.failData,
  fn: (error): NotificationPayload => ({
    type: 'error',
    description: error?.body?.message ?? 'Request failed',
  }),
  target: showNotification,
})
